import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  DollarSign, 
  Calendar,
  User,
  Car,
  TrendingUp,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  FileText,
  Download,
  Loader2
} from 'lucide-react';
import { apiService } from '../../services/api';

export const ReceitasPageNova = () => {
  const [extratoClientes, setExtratoClientes] = useState([]);
  const [historicoMensal, setHistoricoMensal] = useState([]);
  const [totais, setTotais] = useState({});
  const [totalGeral, setTotalGeral] = useState(0);
  const [loading, setLoading] = useState(true);
  const [abaAtiva, setAbaAtiva] = useState('mensal');
  const [mesSelecionado, setMesSelecionado] = useState('');
  const [clienteExpandido, setClienteExpandido] = useState(null);
  const [mesExpandido, setMesExpandido] = useState(null);
  const [detalhesMes, setDetalhesMes] = useState(null);
  const [loadingDetalhesMes, setLoadingDetalhesMes] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  useEffect(() => {
    carregarDados();
  }, [mesSelecionado]);

  const baixarPdf = async (tipo, mes = null) => {
    const key = tipo + (mes || '');
    setDownloadingPdf(key);
    try {
      let url = '';
      if (tipo === 'faturamento') {
        url = '/relatorios/faturamento/pdf';
      } else if (tipo === 'mensal') {
        url = `/relatorios/receita-mensal/pdf?mes=${mes}`;
      } else if (tipo === 'cliente') {
        url = `/relatorios/receita-cliente/pdf${mes ? `?mes=${mes}` : ''}`;
      }
      const response = await apiService.api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const disposition = response.headers['content-disposition'];
      let filename = `relatorio_${tipo}.pdf`;
      if (disposition) {
        const match = disposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      alert('Erro ao gerar o relatório PDF. Tente novamente.');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [extrato, historico] = await Promise.all([
        apiService.api.get(`/receitas/extrato-clientes${mesSelecionado ? `?mes=${mesSelecionado}` : ''}`),
        apiService.api.get('/receitas/historico-mensal')
      ]);
      
      setExtratoClientes(extrato.data.clientes || []);
      setTotais(extrato.data.totais || {});
      setHistoricoMensal(historico.data.historico || []);
      setTotalGeral(historico.data.total_geral || 0);
    } catch (error) {
      console.error('Erro ao carregar receitas:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarDetalhesMes = async (mes) => {
    if (mesExpandido === mes) {
      setMesExpandido(null);
      setDetalhesMes(null);
      return;
    }
    
    setLoadingDetalhesMes(true);
    setMesExpandido(mes);
    try {
      const response = await apiService.api.get(`/receitas/extrato-clientes?mes=${mes}`);
      setDetalhesMes(response.data.clientes || []);
    } catch (error) {
      console.error('Erro ao carregar detalhes do mês:', error);
      setDetalhesMes([]);
    } finally {
      setLoadingDetalhesMes(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      // Tenta diferentes formatos de data
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        // Se falhar, tenta adicionar timezone
        const dateWithTz = new Date(dateStr + 'T00:00:00');
        if (isNaN(dateWithTz.getTime())) return '-';
        return dateWithTz.toLocaleDateString('pt-BR');
      }
      return date.toLocaleDateString('pt-BR');
    } catch {
      return '-';
    }
  };

  const formatMes = (mesStr) => {
    if (!mesStr) return '-';
    const [ano, mes] = mesStr.split('-');
    const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return `${meses[parseInt(mes) - 1]} ${ano}`;
  };

  const formatMesShort = (mesStr) => {
    if (!mesStr) return '-';
    const [ano, mes] = mesStr.split('-');
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${meses[parseInt(mes) - 1]}/${ano}`;
  };

  // Gerar lista de meses para filtro
  const mesesDisponiveis = historicoMensal.map(h => h.mes);

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="receitas-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-emerald-400" />
            Receitas
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Extrato detalhado de receitas por cliente e histórico mensal
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => baixarPdf('faturamento')}
            variant="outline"
            size="sm"
            disabled={!!downloadingPdf}
            data-testid="btn-pdf-faturamento"
            className="text-xs"
          >
            {downloadingPdf === 'faturamento' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Faturamento PDF
          </Button>
          <Button
            onClick={() => baixarPdf('cliente', mesSelecionado || null)}
            variant="outline"
            size="sm"
            disabled={!!downloadingPdf}
            data-testid="btn-pdf-clientes"
            className="text-xs"
          >
            {downloadingPdf?.startsWith('cliente') ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Extrato Clientes PDF
          </Button>
          <Button onClick={carregarDados} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-600 to-teal-700 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 text-emerald-400" />
              <span className="text-xs font-medium text-emerald-400">Total Recebido</span>
            </div>
            <p className="text-xl font-bold text-emerald-300">
              {formatCurrency(totalGeral)}
            </p>
            <p className="text-xs text-emerald-400 mt-1">
              Todo o período
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-blue-400" />
              <span className="text-xs font-medium text-blue-400">Meses Ativos</span>
            </div>
            <p className="text-xl font-bold text-blue-300">
              {historicoMensal.length}
            </p>
            <p className="text-xs text-blue-400 mt-1">
              Com receitas
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-600 to-violet-700 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4 text-purple-400" />
              <span className="text-xs font-medium text-purple-400">Clientes</span>
            </div>
            <p className="text-xl font-bold text-purple-300">
              {totais.quantidade_clientes || 0}
            </p>
            <p className="text-xs text-purple-400 mt-1">
              {mesSelecionado ? formatMesShort(mesSelecionado) : 'Total'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-amber-600 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-orange-600" />
              <span className="text-xs font-medium text-orange-600">Multas + Juros</span>
            </div>
            <p className="text-xl font-bold text-orange-700">
              {formatCurrency(totais.total_multas_juros)}
            </p>
            <p className="text-xs text-orange-600 mt-1">
              {mesSelecionado ? formatMesShort(mesSelecionado) : 'Total'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Abas */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setAbaAtiva('mensal')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            abaAtiva === 'mensal' 
              ? 'border-green-600 text-emerald-400' 
              : 'border-transparent text-gray-400 hover:text-gray-300'
          }`}
        >
          <Calendar className="h-4 w-4 inline mr-2" />
          Histórico Mensal
        </button>
        <button
          onClick={() => setAbaAtiva('clientes')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            abaAtiva === 'clientes' 
              ? 'border-green-600 text-emerald-400' 
              : 'border-transparent text-gray-400 hover:text-gray-300'
          }`}
        >
          <User className="h-4 w-4 inline mr-2" />
          Por Cliente
        </button>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : abaAtiva === 'mensal' ? (
        /* Histórico Mensal - Cards Expansíveis */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-400" />
              Receitas por Mês
              <span className="text-sm font-normal text-gray-400 ml-2">(toque para expandir)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historicoMensal.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhuma receita registrada ainda</p>
              </div>
            ) : (
              <div className="space-y-3">
                {historicoMensal.map((mes, idx) => (
                  <div key={mes.mes} className="border rounded-lg overflow-hidden">
                    {/* Cabeçalho do Mês - Clicável */}
                    <div
                      onClick={() => carregarDetalhesMes(mes.mes)}
                      className={`p-4 cursor-pointer transition-colors ${
                        idx === 0 ? 'bg-green-50 hover:bg-green-100' : 'bg-asphalt-700 hover:bg-asphalt-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            idx === 0 ? 'bg-green-200' : 'bg-gray-200'
                          }`}>
                            <Calendar className={`h-5 w-5 ${idx === 0 ? 'text-emerald-300' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className="font-semibold text-white">{formatMes(mes.mes)}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <span>{mes.quantidade_pagamentos} pagamento(s)</span>
                              {idx === 0 && (
                                <Badge className="bg-green-100 text-emerald-300 text-xs">Mês atual</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-lg font-bold text-emerald-400">
                              {formatCurrency(mes.total_recebido)}
                            </p>
                            <div className="text-xs text-gray-400 hidden sm:block">
                              {mes.valor_multas > 0 || mes.valor_juros > 0 ? (
                                <span className="text-orange-600">
                                  +{formatCurrency((mes.valor_multas || 0) + (mes.valor_juros || 0))} extras
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); baixarPdf('mensal', mes.mes); }}
                            disabled={!!downloadingPdf}
                            className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
                            title="Baixar PDF deste mês"
                            data-testid={`btn-pdf-mes-${mes.mes}`}
                          >
                            {downloadingPdf === `mensal${mes.mes}` ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : <Download className="h-4 w-4 text-gray-400" />}
                          </button>
                          {mesExpandido === mes.mes ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {/* Resumo em linha para mobile */}
                      <div className="flex flex-wrap gap-3 mt-3 text-xs">
                        <span className="text-blue-400">Aluguéis: {formatCurrency(mes.valor_alugueis)}</span>
                        {mes.valor_multas > 0 && <span className="text-rose-400">Multas: {formatCurrency(mes.valor_multas)}</span>}
                        {mes.valor_juros > 0 && <span className="text-orange-600">Juros: {formatCurrency(mes.valor_juros)}</span>}
                        {mes.valor_debitos > 0 && <span className="text-purple-400">Débitos: {formatCurrency(mes.valor_debitos)}</span>}
                      </div>
                    </div>
                    
                    {/* Detalhes Expandidos - Pagamentos por Cliente */}
                    {mesExpandido === mes.mes && (
                      <div className="border-t bg-asphalt-800 p-4">
                        {loadingDetalhesMes ? (
                          <div className="flex items-center justify-center py-6">
                            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                            <span className="ml-2 text-gray-400">Carregando...</span>
                          </div>
                        ) : detalhesMes && detalhesMes.length > 0 ? (
                          <div className="space-y-4">
                            <p className="text-sm font-medium text-gray-300 flex items-center gap-2">
                              <User className="h-4 w-4" />
                              Pagamentos por Cliente em {formatMes(mes.mes)}
                            </p>
                            {detalhesMes.map((cliente) => (
                              <div key={cliente.cliente_id} className="border rounded-lg bg-asphalt-700 overflow-hidden">
                                <div className="p-3 bg-asphalt-700 flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                      <User className="h-4 w-4 text-emerald-400" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-white text-sm">{cliente.cliente_nome}</p>
                                      <p className="text-xs text-gray-400 flex items-center gap-1">
                                        <Car className="h-3 w-3" />
                                        {cliente.moto_placa} • {cliente.quantidade_pagamentos} pag.
                                      </p>
                                    </div>
                                  </div>
                                  <p className="font-bold text-emerald-400">{formatCurrency(cliente.total_recebido)}</p>
                                </div>
                                
                                <div className="p-3 space-y-2">
                                  {cliente.pagamentos.map((pag, idx) => (
                                    <div 
                                      key={pag.id || idx}
                                      className="flex items-center justify-between p-2 bg-asphalt-800 rounded border text-sm"
                                    >
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <Badge variant="outline" className="text-xs">Sem. {pag.semana}</Badge>
                                          <span className="text-gray-400 text-xs">{formatDate(pag.data_pagamento)}</span>
                                          {pag.pago_com_juros && (
                                            <Badge className="bg-orange-100 text-orange-700 text-xs">Juros</Badge>
                                          )}
                                        </div>
                                        <div className="text-xs text-gray-400 mt-1">
                                          Aluguel: {formatCurrency(pag.valor_aluguel)}
                                          {pag.multa > 0 && <span className="text-rose-400 ml-2">Multa: {formatCurrency(pag.multa)}</span>}
                                          {pag.juros > 0 && <span className="text-orange-600 ml-2">Juros: {formatCurrency(pag.juros)}</span>}
                                          {pag.debitos > 0 && <span className="text-purple-400 ml-2">Déb: {formatCurrency(pag.debitos)}</span>}
                                        </div>
                                      </div>
                                      <span className="font-bold text-emerald-400">{formatCurrency(pag.valor_pago)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-gray-400 py-4">Nenhum pagamento encontrado neste mês</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Total Geral */}
                <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-green-800">TOTAL GERAL</p>
                        <p className="text-xs text-emerald-400">{historicoMensal.length} mês(es) • {historicoMensal.reduce((acc, m) => acc + m.quantidade_pagamentos, 0)} pagamentos</p>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-emerald-300">{formatCurrency(totalGeral)}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Extrato por Cliente */
        <div className="space-y-4">
          {/* Filtro por mês */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <select
              value={mesSelecionado}
              onChange={(e) => setMesSelecionado(e.target.value)}
              className="text-base border border-white/20 rounded-lg px-4 py-3 bg-asphalt-800 appearance-none cursor-pointer min-w-[180px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              style={{ WebkitAppearance: 'menulist', fontSize: '16px' }}
              data-testid="filtro-mes-select"
            >
              <option value="">Todos os meses</option>
              {mesesDisponiveis.map(mes => (
                <option key={mes} value={mes}>{formatMes(mes)}</option>
              ))}
            </select>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-emerald-400" />
                Extrato por Cliente
                {mesSelecionado && (
                  <Badge variant="secondary" className="ml-2">{formatMes(mesSelecionado)}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {extratoClientes.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum cliente com pagamentos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {extratoClientes.map((cliente) => (
                    <div
                      key={cliente.cliente_id}
                      className="border rounded-lg overflow-hidden"
                    >
                      {/* Cabeçalho do cliente */}
                      <div
                        className="flex items-center justify-between p-4 bg-asphalt-700 cursor-pointer hover:bg-asphalt-700 transition-colors"
                        onClick={() => setClienteExpandido(
                          clienteExpandido === cliente.cliente_id ? null : cliente.cliente_id
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-emerald-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{cliente.cliente_nome}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Car className="h-3 w-3" />
                              <span>{cliente.moto_placa}</span>
                              <span>•</span>
                              <span>{cliente.quantidade_pagamentos} pagamento(s)</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-lg font-bold text-emerald-400">
                              {formatCurrency(cliente.total_recebido)}
                            </p>
                            {cliente.total_multas_juros > 0 && (
                              <p className="text-xs text-orange-600">
                                +{formatCurrency(cliente.total_multas_juros)} (multa/juros)
                              </p>
                            )}
                          </div>
                          {clienteExpandido === cliente.cliente_id ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Detalhes expandidos */}
                      {clienteExpandido === cliente.cliente_id && (
                        <div className="p-4 border-t bg-asphalt-800">
                          <div className="space-y-2">
                            {cliente.pagamentos.map((pag, idx) => (
                              <div
                                key={pag.id || idx}
                                className="flex items-center justify-between p-3 bg-asphalt-700 rounded-lg text-sm"
                              >
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">
                                      Semana {pag.semana}
                                    </Badge>
                                    <span className="text-gray-400">
                                      {formatDate(pag.data_pagamento)}
                                    </span>
                                    {pag.pago_com_juros && (
                                      <Badge className="bg-orange-100 text-orange-700 text-xs">
                                        Com juros
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1 space-x-2">
                                    <span>Aluguel: {formatCurrency(pag.valor_aluguel)}</span>
                                    {pag.multa > 0 && (
                                      <span className="text-rose-400">Multa: {formatCurrency(pag.multa)}</span>
                                    )}
                                    {pag.juros > 0 && (
                                      <span className="text-orange-600">Juros: {formatCurrency(pag.juros)}</span>
                                    )}
                                    {pag.debitos > 0 && (
                                      <span className="text-purple-400">Débitos: {formatCurrency(pag.debitos)}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className="font-bold text-emerald-400">
                                    {formatCurrency(pag.valor_pago)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Totais do cliente */}
                          <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-xs text-gray-400">Total Recebido</p>
                              <p className="font-bold text-emerald-400">{formatCurrency(cliente.total_recebido)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">Multas + Juros</p>
                              <p className="font-bold text-orange-600">{formatCurrency(cliente.total_multas_juros)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">Débitos Pagos</p>
                              <p className="font-bold text-purple-400">{formatCurrency(cliente.total_debitos)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReceitasPageNova;
